import { useState, useEffect, useRef } from "react";
import { X, Loader2, Search, Award, Camera, CameraOff, ScanLine, ExternalLink, ImageIcon, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { Html5Qrcode, Html5QrcodeSupportedFormats } from "html5-qrcode";
import api, { formatApiError } from "@/lib/api";

const fmt = (n) => `$${Number(n || 0).toFixed(2)}`;
const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

const emptyForm = {
  name: "", set_name: "", card_number: "", grade: "", image_url: "",
  tcg_price: 0, market_value: 0, quantity: 1,
};

export default function SlabModal({ open, onClose, onSaved, onCapture, captureLabel, title }) {
  const [cert, setCert] = useState("");
  const [loading, setLoading] = useState(false);
  const [found, setFound] = useState(false);
  const [saving, setSaving] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [scanMode, setScanMode] = useState(null); // 'native' | 'fallback'
  const [refreshing, setRefreshing] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const scannerRef = useRef(null);
  const certRef = useRef(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const rafRef = useRef(null);

  useEffect(() => {
    if (open) {
      setCert(""); setForm(emptyForm); setFound(false); setScanning(false);
      setTimeout(() => certRef.current?.focus(), 80);
    }
  }, [open]);

  const stopCamera = () => {
    if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
    if (streamRef.current) { streamRef.current.getTracks().forEach((t) => t.stop()); streamRef.current = null; }
    if (videoRef.current) { try { videoRef.current.srcObject = null; } catch { /* ignore */ } }
    const s = scannerRef.current;
    if (s) {
      scannerRef.current = null;
      // html5-qrcode's stop() THROWS synchronously if the scanner never started (e.g. camera denied) —
      // an uncaught throw here (effect cleanup) unmounts the whole app.
      try { Promise.resolve(s.stop()).then(() => { try { s.clear(); } catch { /* ignore */ } }).catch(() => {}); }
      catch { try { s.clear(); } catch { /* ignore */ } }
    }
  };

  useEffect(() => {
    if (!scanning) { stopCamera(); setScanMode(null); return; }
    let cancelled = false;

    const onDecode = (raw) => {
      const digits = (raw || "").toString().replace(/\D/g, "");
      if (!digits || cancelled) return;
      setScanning(false);
      lookup(digits);
    };

    // Native BarcodeDetector decodes Code-128 (PSA slab barcodes) far more reliably
    // than html5-qrcode's camera path; fall back to html5-qrcode when unavailable.
    const startNative = async () => {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } });
      if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return; }
      streamRef.current = stream;
      setScanMode("native");
      const video = videoRef.current;
      video.srcObject = stream;
      video.setAttribute("playsinline", "true");
      await video.play().catch(() => {});
      const detector = new window.BarcodeDetector({ formats: ["code_128", "code_39", "qr_code", "ean_13"] });
      const tick = async () => {
        if (cancelled) return;
        try {
          const codes = await detector.detect(video);
          if (codes && codes.length) { onDecode(codes[0].rawValue); return; }
        } catch { /* transient frame error — keep polling */ }
        rafRef.current = requestAnimationFrame(tick);
      };
      rafRef.current = requestAnimationFrame(tick);
    };

    const startFallback = () => {
      setScanMode("fallback");
      setTimeout(() => {
        if (cancelled) return;
        const scanner = new Html5Qrcode("slab-scan-region", {
          verbose: false,
          formatsToSupport: [Html5QrcodeSupportedFormats.CODE_128, Html5QrcodeSupportedFormats.CODE_39, Html5QrcodeSupportedFormats.QR_CODE],
        });
        scannerRef.current = scanner;
        scanner.start({ facingMode: "environment" }, { fps: 15, qrbox: { width: 300, height: 150 } },
          (text) => onDecode(text), () => {})
          .then(() => {
            // user stopped/closed while the camera was still starting — shut it down now
            if (cancelled || scannerRef.current !== scanner) { try { scanner.stop().then(() => scanner.clear()).catch(() => {}); } catch { /* ignore */ } }
          })
          .catch(() => { if (scannerRef.current === scanner) scannerRef.current = null; if (!cancelled) toast.error("Could not start the camera. Type the cert number above instead."); });
      }, 60);
    };

    (async () => {
      let supported = false;
      try {
        if ("BarcodeDetector" in window) {
          const fmts = await window.BarcodeDetector.getSupportedFormats();
          supported = Array.isArray(fmts) && fmts.includes("code_128");
        }
      } catch { supported = false; }
      if (supported) {
        try { await startNative(); return; }
        catch { stopCamera(); }
      }
      startFallback();
    })();

    return () => { cancelled = true; stopCamera(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scanning]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const prefillResult = (data) => {
    // Fill metadata from the cached record while the newest graded price is being refreshed.
    setForm((f) => ({
      ...f,
      name: data.name || f.name,
      set_name: data.set_name || f.set_name,
      card_number: data.card_number || f.card_number,
      grade: data.grade || f.grade,
      image_url: data.image_front || f.image_url,
      market_value: data.market_value != null ? round2(data.market_value) : f.market_value,
      tcg_price: data.market_value != null ? round2(data.market_value) : f.tcg_price,
    }));
    setFound(true);
  };

  const applyResult = (data) => {
    prefillResult(data);
    if (data.unresolved) {
      // Cert verified but CardOS couldn't resolve the card — grade/cert pulled, user adds the name.
      toast.info(`${data.grade || "Slab"} verified · add the card name & price`);
      return;
    }
    const note = data.price_stale ? " (last known price)" : (data.cached ? " (cached)" : "");
    toast.success(`${data.grade || "Slab"} · ${data.name}${data.market_value != null ? ` · ${fmt(data.market_value)}` : ""}${note}`);
  };

  const lookup = async (value) => {
    const c = (value ?? cert).toString().replace(/\D/g, "");
    if (!c) { toast.error("Enter a cert number"); return; }
    setCert(c);
    setLoading(true);
    setRefreshing(false);
    let prefilled = false;
    const started = Date.now();
    const poll = async () => {
      try {
        const { data } = await api.get(`/psa/cert/${c}`);
        if (data.found) { applyResult(data); setLoading(false); setRefreshing(false); return; }
        if (data.refreshing) {
          setRefreshing(true);
          if (!prefilled && data.name) { prefillResult(data); prefilled = true; }
        }
        if (data.pending && Date.now() - started < 150000) { setTimeout(poll, 3000); return; }
        // not pending (transient error) or we gave up waiting — let them enter manually
        setFound(true); setLoading(false); setRefreshing(false);
        toast.error(data.error || (data.pending ? "Lookup is taking too long — enter details manually." : "No slab record found — enter the details manually."));
      } catch (e) {
        setFound(true); setLoading(false); setRefreshing(false);
        toast.error(formatApiError(e.response?.data?.detail) || "Lookup failed — enter details manually.");
      }
    };
    poll();
  };

  const onCertKey = (e) => { if (e.key === "Enter") { e.preventDefault(); lookup(); } };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Card name is required"); return; }
    // Capture mode: hand the slab details back to the parent (Buying lot / Trade-in) instead
    // of saving straight to inventory.
    if (onCapture) {
      onCapture({
        name: form.name, set_name: form.set_name, card_number: form.card_number,
        grade: form.grade, image_url: form.image_url, cert_number: cert,
        grading_company: "PSA", item_type: "slab",
        // Fall back to the Sale Price when no Market Value was entered so the lot/trade line isn't $0.
        market_value: round2(Number(form.market_value) > 0 ? form.market_value : form.tcg_price), tcg_price: round2(form.tcg_price),
        quantity: Number(form.quantity) || 1,
      });
      onClose();
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name, set_name: form.set_name, card_number: form.card_number,
        condition: "NM", grade: form.grade, image_url: form.image_url,
        item_type: "slab", grading_company: "PSA", cert_number: cert, sku: cert,
        quantity: Number(form.quantity) || 1,
        tcg_price: round2(form.tcg_price), market_value: round2(form.market_value),
        price_override: true, on_tcgplayer: false, on_shopify: false, category: "pokemon",
      };
      const { data } = await api.post("/cards", payload);
      toast.success(`Added slab ${form.grade || ""} ${form.name}`);
      onSaved(data, true);
      onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (!open) return null;
  const ladderUrl = `https://app.cardladder.com/search?query=${encodeURIComponent(`${form.name} ${form.grade}`.trim())}`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(5,7,12,0.8)", backdropFilter: "blur(4px)" }}>
      <div className="w-full max-w-2xl rounded-2xl border max-h-[92vh] overflow-y-auto animate-fade-up" style={{ background: "#0D1017", borderColor: "var(--pk-border)" }} data-testid="slab-modal">
        <div className="flex items-center justify-between px-6 py-4 border-b sticky top-0 z-20" style={{ background: "#0D1017", borderColor: "var(--pk-border)" }}>
          <h3 className="font-heading text-lg font-bold text-slate-100 flex items-center gap-2"><Award className="h-5 w-5 text-violet-400" /> {title || "Add PSA Slab"}</h3>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-200" data-testid="slab-modal-close"><X className="h-5 w-5" /></button>
        </div>

        <div className="px-6 pt-5">
          <label className="text-xs font-semibold uppercase tracking-wider text-slate-400 flex items-center gap-1.5"><ScanLine className="h-3.5 w-3.5 text-violet-400" /> PSA Cert Number</label>
          <div className="flex gap-2 mt-1.5">
            <div className="flex items-center gap-2 px-3 py-2.5 rounded-xl border flex-1 focus-within:ring-2 focus-within:ring-violet-500/50" style={{ background: "var(--pk-card)", borderColor: "var(--pk-border)" }}>
              <Search className="h-4 w-4 text-slate-500 shrink-0" />
              <input ref={certRef} value={cert} onChange={(e) => setCert(e.target.value.replace(/\D/g, ""))} onKeyDown={onCertKey}
                data-testid="slab-cert-input" placeholder="Type or scan the cert number, then Enter"
                className="w-full bg-transparent outline-none text-sm text-slate-100 placeholder:text-slate-600 font-mono-num" />
            </div>
            <button onClick={() => lookup()} disabled={loading || !cert} data-testid="slab-lookup-btn"
              className="px-4 py-2.5 rounded-xl bg-violet-500 text-white font-semibold text-sm hover:bg-violet-400 disabled:opacity-50 flex items-center gap-2">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />} Look up
            </button>
            <button onClick={() => setScanning((s) => !s)} data-testid="slab-scan-toggle" title="Scan barcode with camera"
              className={`px-3 py-2.5 rounded-xl border text-sm flex items-center gap-2 ${scanning ? "border-rose-700/60 text-rose-400" : "border-[var(--pk-border)] text-slate-300 hover:bg-[var(--pk-hover)]"}`} style={{ borderColor: scanning ? undefined : "var(--pk-border)" }}>
              {scanning ? <CameraOff className="h-4 w-4" /> : <Camera className="h-4 w-4" />}
            </button>
          </div>
          {scanning && (
            <div className="mt-3">
              <video ref={videoRef} data-testid="slab-scan-video" muted playsInline
                className="w-full rounded-xl border block bg-black" style={{ borderColor: "var(--pk-border)", maxHeight: 260, objectFit: "cover", display: scanMode === "fallback" ? "none" : "block" }} />
              <div id="slab-scan-region" className="w-full rounded-xl overflow-hidden border" style={{ borderColor: "var(--pk-border)", minHeight: 180, display: scanMode === "fallback" ? "block" : "none" }} />
              <p className="text-[11px] text-slate-500 mt-1.5 text-center" data-testid="slab-scan-hint">Hold the slab's barcode flat and centered in the frame. If it won't read, type the cert number above.</p>
            </div>
          )}
          {loading && (
            <div className="flex items-center gap-2 mt-3 px-3 py-2.5 rounded-xl border text-xs text-violet-300" style={{ background: "rgba(139,92,246,0.08)", borderColor: "rgba(139,92,246,0.3)" }} data-testid="slab-loading">
              <Loader2 className="h-4 w-4 animate-spin shrink-0" />
              Looking up the slab… the first scan of a cert can take up to a minute while the grader verifies it. Repeat scans are instant.
            </div>
          )}
          <p className="text-[11px] text-slate-500 mt-1.5">Camera reads the slab's Code-128 barcode, or a USB/handheld scanner types it straight in. Cert data (grade, image & graded price) is cached so repeat lookups are instant and don't spend extra credits.</p>
        </div>

        {found && (
          <div className="px-6 py-5">
            {form.name.trim() === "" && (
              <div className="mb-4 flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-medium text-amber-300" style={{ background: "rgba(245,158,11,0.1)", borderColor: "rgba(245,158,11,0.35)" }} data-testid="slab-unresolved-badge">
                <AlertTriangle className="h-4 w-4 shrink-0" />
                Grade verified, but the card couldn't be identified — type the card name below before saving.
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-[150px_1fr] gap-5">
              <div className="rounded-xl border overflow-hidden bg-[var(--pk-card)] flex items-center justify-center" style={{ borderColor: "var(--pk-border)", minHeight: 190 }} data-testid="slab-image">
                {form.image_url ? <img src={form.image_url} alt="slab" className="w-full h-full object-contain" /> : <ImageIcon className="h-8 w-8 text-slate-700" />}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field className="col-span-2" label="Card Name" value={form.name} onChange={(v) => set("name", v)} testid="slab-name" />
                <Field label="Set / Brand" value={form.set_name} onChange={(v) => set("set_name", v)} testid="slab-set" />
                <Field label="Card #" value={form.card_number} onChange={(v) => set("card_number", v)} testid="slab-number" />
                <Field label="Grade" value={form.grade} onChange={(v) => set("grade", v)} testid="slab-grade" />
                <Field label="Quantity" type="number" value={form.quantity} onChange={(v) => set("quantity", v)} testid="slab-qty" />
                <Field label="Sale Price ($)" type="number" value={form.tcg_price} onChange={(v) => set("tcg_price", v)} testid="slab-price" />
                <div>
                  <Label>Market Value ($)</Label>
                  <div className="flex gap-1.5">
                    <input type="number" value={form.market_value} onChange={(e) => set("market_value", e.target.value)} data-testid="slab-market-value"
                      className="w-full px-3 py-2.5 rounded-lg border text-sm text-slate-100 outline-none focus:ring-2 focus:ring-violet-500/50 font-mono-num" style={{ background: "var(--pk-card)", borderColor: "var(--pk-border)" }} />
                    <a href={ladderUrl} target="_blank" rel="noreferrer" data-testid="slab-cardladder-link" title="Look up value on CardLadder"
                      className="shrink-0 px-2.5 rounded-lg border flex items-center text-slate-300 hover:bg-[var(--pk-hover)]" style={{ borderColor: "var(--pk-border)" }}>
                      <ExternalLink className="h-4 w-4" />
                    </a>
                  </div>
                  <p className="text-[10px] text-slate-500 mt-1">Auto-filled from the graded price — edit or check CardLadder via the link.</p>
                  {refreshing && (
                    <p className="text-[10px] text-violet-400 mt-1 flex items-center gap-1" data-testid="slab-price-refreshing">
                      <Loader2 className="h-3 w-3 animate-spin" /> Fetching the latest graded market value…
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="px-6 py-4 border-t flex justify-end gap-3 sticky bottom-0" style={{ background: "#0D1017", borderColor: "var(--pk-border)" }}>
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm text-slate-300 hover:bg-[var(--pk-hover)] border" style={{ borderColor: "var(--pk-border)" }}>Cancel</button>
          <button onClick={save} disabled={saving || !found} data-testid="slab-save-btn" className="px-5 py-2 rounded-lg text-sm font-semibold bg-violet-500 text-white hover:bg-violet-400 disabled:opacity-50 flex items-center gap-2">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />} {captureLabel || "Save Slab"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Label({ children }) {
  return <span className="text-xs font-semibold uppercase tracking-wider text-slate-400 block mb-1.5">{children}</span>;
}
function Field({ label, value, onChange, type = "text", testid, className = "" }) {
  return (
    <div className={className}>
      <Label>{label}</Label>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="w-full px-3 py-2.5 rounded-lg border text-sm text-slate-100 outline-none focus:ring-2 focus:ring-violet-500/50" style={{ background: "var(--pk-card)", borderColor: "var(--pk-border)" }} />
    </div>
  );
}
